import { useCallback, useMemo, useRef, useState } from "react";

import { useLocalization } from "@fluent/react";
import { useBreakpoints } from "@narmi/design_system";
import Account from "byzantine/src/Account";

import { useAccountsContext } from "../../../components/contexts/AccountContext";
import { useCurrentUserBalanceChartStorage } from "../../../components/dashboard/Charts/storage";

import useViewPreference from "./useViewPreference";
import LinkedAccountsSection from "./LinkedAccountsSection";
import AccountsSectionHeader from "./AccountsSectionShared/AccountsSectionHeader";
import AccountsList from "./AccountsSectionShared/AccountsList";
import HiddenAccountsSection from "./AccountsSectionShared/HiddenAccountsSection";

import type {
  AccountClassType,
  FilterKey,
} from "./AccountsSectionShared/types";

const FILTER_PRODUCT_GROUPS: Partial<Record<FilterKey, readonly string[]>> = {
  checking: Account.PRODUCT_GROUPS.Checking,
  savings: Account.PRODUCT_GROUPS.Savings,
  credit_cards: Account.PRODUCT_GROUPS["Credit cards"],
  loans: Account.PRODUCT_GROUPS.Loans,
  cds: Account.PRODUCT_GROUPS.CDs,
};

// Filters other than "all" and "favorites" should only be offered when the
// user actually has at least one visible account in that product group.
const getAvailableFilters = (accounts: AccountClassType[]): FilterKey[] => {
  const productSpecificFilters = (
    Object.keys(FILTER_PRODUCT_GROUPS) as FilterKey[]
  ).filter((key) => {
    const group = FILTER_PRODUCT_GROUPS[key];
    return (
      group && accounts.some((a) => group.includes(a.product_type as string))
    );
  });
  return ["all", "favorites", ...productSpecificFilters];
};

const applyFilter = (
  accounts: AccountClassType[],
  filter: FilterKey
): AccountClassType[] => {
  if (filter === "all") return accounts;
  if (filter === "favorites") return accounts.filter((a) => a.favorited);
  const group = FILTER_PRODUCT_GROUPS[filter];
  return group
    ? accounts.filter((a) => group.includes(a.product_type as string))
    : accounts;
};

interface AccountsModuleProps {
  additionalExternalAccounts?: AccountClassType[];
}

const AccountsModule = ({
  additionalExternalAccounts = [],
}: AccountsModuleProps) => {
  const { l10n } = useLocalization();
  const { m: isMediumViewport } = useBreakpoints();
  const isSmall = !isMediumViewport;
  const { internalAccounts } = useAccountsContext();
  const [internalView, setInternalView] = useViewPreference("internal");

  // On small screens, always use list view
  const effectiveInternalView = isSmall ? "list" : internalView;
  const [filter, setFilter] = useState<FilterKey>("all");
  const [isEditingInternal, setIsEditingInternal] = useState(false);
  const savedFilterRef = useRef<FilterKey>("all");
  const [chartPreferences, setChartPreferences] =
    useCurrentUserBalanceChartStorage();
  const showHiddenBalance = chartPreferences.includeHiddenAccounts;
  const setShowHiddenBalance = useCallback(
    (includeHiddenAccounts: boolean) => {
      setChartPreferences((previous) => ({
        ...previous,
        includeHiddenAccounts,
      }));
    },
    [setChartPreferences]
  );

  const internalAccountsKey = internalAccounts
    .map((a) => `${a.id}:${a.favorited}:${a.hidden}`)
    .join(",");
  const visibleInternalAccounts = useMemo(
    () =>
      internalAccounts
        .filter((a: Account) => !a.hidden)
        .sort(
          (a: Account, b: Account) =>
            (b.favorited ? 1 : 0) - (a.favorited ? 1 : 0)
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [internalAccountsKey]
  );

  const hiddenAccountsKey = internalAccounts
    .map((a) => `${a.id}:${a.hidden}`)
    .join(",");
  const hiddenInternalAccounts = useMemo(
    () => internalAccounts.filter((a) => a.hidden),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hiddenAccountsKey]
  );

  const filteredInternalAccounts = applyFilter(visibleInternalAccounts, filter);
  const displayedInternalAccounts =
    effectiveInternalView === "list"
      ? visibleInternalAccounts
      : filteredInternalAccounts;
  const availableFilters = useMemo(
    () => getAvailableFilters(visibleInternalAccounts),
    [visibleInternalAccounts]
  );

  const toggleEditInternal = useCallback(() => {
    setIsEditingInternal((prev) => {
      if (!prev) {
        savedFilterRef.current = filter;
        setFilter("all");
      } else {
        setFilter(savedFilterRef.current);
      }
      return !prev;
    });
  }, [filter]);

  return (
    <div>
      {/* My Accounts */}
      <AccountsSectionHeader
        title={l10n.getString("dashboard-redesign-accounts-my-accounts")}
        view={effectiveInternalView}
        onViewChange={setInternalView}
        isEditing={isEditingInternal}
        onEditToggle={toggleEditInternal}
        showPlusButton
        filter={filter}
        onFilterChange={setFilter}
        availableFilters={availableFilters}
        isSmall={isSmall}
      />

      {displayedInternalAccounts.length === 0 ? (
        <p className="fontSize--s fontColor--secondary">
          {l10n.getString(
            filter !== "all" && effectiveInternalView === "card"
              ? "dashboard-redesign-accounts-no-filter-results"
              : "dashboard-redesign-accounts-no-accounts"
          )}
        </p>
      ) : (
        <AccountsList
          accounts={displayedInternalAccounts}
          view={effectiveInternalView}
          isEditing={isEditingInternal}
          useExistingListView={!isSmall}
        />
      )}

      {/* Hidden Accounts (shown when editing) */}
      {isEditingInternal && effectiveInternalView === "card" && (
        <HiddenAccountsSection
          accounts={hiddenInternalAccounts}
          view={effectiveInternalView}
          showHiddenBalance={showHiddenBalance}
          onShowHiddenBalanceChange={setShowHiddenBalance}
        />
      )}

      <LinkedAccountsSection
        additionalExternalAccounts={additionalExternalAccounts}
      />
    </div>
  );
};

export default AccountsModule;
